const isBrowser = typeof window !== "undefined";

const getEnv = () => {
  try {
    return typeof import.meta !== "undefined" ? import.meta.env : process.env;
  } catch {
    return {};
  }
};

const ENV = getEnv();
const IS_DEV = Boolean(ENV?.DEV);

const logDebug = (...args) => {
  if (IS_DEV) {
    console.debug("[userSession]", ...args);
  }
};

const SESSION_KEY = "user_email";
const SESSION_USER_ID_KEY = "user_id";
export const USER_EMAIL_EVENT = "optra:user-email-change";
export const ENTITLEMENT_CHANGE_EVENT = "optra:entitlement-change";

export const normalizeEmail = (email) => {
  if (typeof email !== "string") return null;
  const trimmed = email.trim();
  if (!trimmed) return null;
  return trimmed.toLowerCase();
};

export const normalizeUserId = (userId) => {
  if (typeof userId !== "string") return null;
  const trimmed = userId.trim();
  if (!trimmed) return null;
  return trimmed;
};

const dispatchSessionEvent = () => {
  if (!isBrowser) return;
  window.dispatchEvent(new CustomEvent(USER_EMAIL_EVENT));
};

export function getSessionEmail() {
  if (!isBrowser) {
    return null;
  }
  try {
    const email = window.sessionStorage.getItem(SESSION_KEY);
    if (email) {
      logDebug("Retrieved session email");
    }
    return normalizeEmail(email) || null;
  } catch (error) {
    if (IS_DEV) {
      console.warn("[userSession] Failed to access sessionStorage", error);
    }
    return null;
  }
}

export function getSessionUserId() {
  if (!isBrowser) {
    return null;
  }
  try {
    const userId = window.sessionStorage.getItem(SESSION_USER_ID_KEY);
    return normalizeUserId(userId) || null;
  } catch (error) {
    if (IS_DEV) {
      console.warn("[userSession] Failed to access sessionStorage user_id", error);
    }
    return null;
  }
}

function getClerkUser() {
  if (!isBrowser) return null;
  try {
    return window.Clerk?.user || window.Clerk?.session?.user || null;
  } catch {
    return null;
  }
}

export function getClerkIdentityFallback() {
  const user = getClerkUser();
  if (!user) return null;
  const userId = normalizeUserId(user.id);
  const email = normalizeEmail(
    user.primaryEmailAddress?.emailAddress ||
      user.emailAddresses?.[0]?.emailAddress ||
      user.email ||
      ""
  );
  const name = String(user.fullName || user.username || user.firstName || "").trim();
  if (!userId && !email) return null;
  return {
    userId,
    email,
    name: name || null,
  };
}

export function setSessionEmail(email) {
  if (!isBrowser) return;
  const normalized = normalizeEmail(email);
  try {
    if (normalized) {
      window.sessionStorage.setItem(SESSION_KEY, normalized);
      logDebug("Stored session email");
    } else {
      window.sessionStorage.removeItem(SESSION_KEY);
      logDebug("Cleared session email (empty value provided)");
    }
  } catch (error) {
    if (IS_DEV) {
      console.warn("[userSession] Unable to persist session email", error);
    }
  } finally {
    dispatchSessionEvent();
  }
}

export function setSessionUserId(userId) {
  if (!isBrowser) return;
  const normalized = normalizeUserId(userId);
  try {
    if (normalized) {
      window.sessionStorage.setItem(SESSION_USER_ID_KEY, normalized);
      logDebug("Stored session user_id");
    } else {
      window.sessionStorage.removeItem(SESSION_USER_ID_KEY);
      logDebug("Cleared session user_id (empty value provided)");
    }
  } catch (error) {
    if (IS_DEV) {
      console.warn("[userSession] Unable to persist session user_id", error);
    }
  } finally {
    dispatchSessionEvent();
  }
}

export function clearSessionEmail() {
  if (!isBrowser) return;
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
    logDebug("Cleared session email");
  } catch (error) {
    if (IS_DEV) {
      console.warn("[userSession] Unable to clear session email", error);
    }
  } finally {
    dispatchSessionEvent();
  }
}

export function clearSessionUserId() {
  if (!isBrowser) return;
  try {
    window.sessionStorage.removeItem(SESSION_USER_ID_KEY);
    logDebug("Cleared session user_id");
  } catch (error) {
    if (IS_DEV) {
      console.warn("[userSession] Unable to clear session user_id", error);
    }
  } finally {
    dispatchSessionEvent();
  }
}

export function getActiveUserIdentity() {
  const clerkIdentity = getClerkIdentityFallback();
  const userId = getSessionUserId() || clerkIdentity?.userId || null;
  const email = getSessionEmail() || clerkIdentity?.email || null;
  if (!userId && !email) return null;
  if (userId) {
    return {
      source: "clerk",
      userId,
      email: email || null,
      name: clerkIdentity?.name || null,
      key: `uid:${userId}`,
    };
  }
  return {
    source: "email",
    userId: null,
    email,
    name: clerkIdentity?.name || null,
    key: `email:${email}`,
  };
}

export function getIdentityStorageSuffix(identity) {
  if (!identity?.key) return null;
  return encodeURIComponent(String(identity.key));
}

export function subscribeToSessionEmail(callback) {
  if (!isBrowser || typeof callback !== "function") {
    return () => {};
  }

  const handler = () => {
    callback(getSessionEmail());
  };

  window.addEventListener(USER_EMAIL_EVENT, handler);
  window.addEventListener("storage", handler);

  return () => {
    window.removeEventListener(USER_EMAIL_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
}

/** "free" | "pro" from access_level cookie */
export function getAccessLevelFromCookie() {
  if (!isBrowser) return "free";
  try {
    const cookies = document.cookie.split(";");
    const c = cookies.find((k) => k.trim().startsWith("access_level="));
    const v = c ? c.split("=")[1]?.trim() : null;
    return v === "pro" ? "pro" : "free";
  } catch {
    return "free";
  }
}

/** "Scale" | "Enterprise" | "Accelerate" | null. When pro but no plan, defaults to Scale. */
export function getPlanFromCookie() {
  if (!isBrowser) return null;
  try {
    const cookies = document.cookie.split(";");
    const c = cookies.find((k) => k.trim().startsWith("plan="));
    const v = c ? c.split("=")[1]?.trim() : null;
    if (v && ["Scale", "Enterprise", "Accelerate"].includes(v)) return v;
    if (getAccessLevelFromCookie() === "pro") return "Scale";
    return null;
  } catch {
    return null;
  }
}

export function dispatchEntitlementChange() {
  if (!isBrowser) return;
  window.dispatchEvent(new CustomEvent(ENTITLEMENT_CHANGE_EVENT));
}

/**
 * Apply tier cookies used by WorkflowPage, useTierGate, and TierLockedView.
 * Does not clear session email when email is omitted.
 */
export function applyTierCookies({ accessLevel, plan, email } = {}) {
  if (!isBrowser) return;
  try {
    if (accessLevel === "pro" && plan && ["Scale", "Enterprise", "Accelerate"].includes(plan)) {
      document.cookie = `access_level=pro; path=/; max-age=3600; SameSite=Lax`;
      document.cookie = `plan=${plan}; path=/; max-age=3600; SameSite=Lax`;
    } else {
      document.cookie = "access_level=; path=/; max-age=0; SameSite=Lax";
      document.cookie = "plan=; path=/; max-age=0; SameSite=Lax";
    }
    const normalized = normalizeEmail(email);
    if (normalized) {
      setSessionEmail(normalized);
    }
  } finally {
    dispatchEntitlementChange();
  }
}

export function clearTierCookies() {
  if (!isBrowser) return;
  try {
    document.cookie = "access_level=; path=/; max-age=0; SameSite=Lax";
    document.cookie = "plan=; path=/; max-age=0; SameSite=Lax";
  } finally {
    dispatchEntitlementChange();
  }
}

export function subscribeToEntitlementChange(callback) {
  if (!isBrowser || typeof callback !== "function") {
    return () => {};
  }
  const handler = () => callback();
  window.addEventListener(ENTITLEMENT_CHANGE_EVENT, handler);
  return () => window.removeEventListener(ENTITLEMENT_CHANGE_EVENT, handler);
}
